/* GrowthOS — Compose Email modal (shared across lead/contact/quote view pages).
   Pixel-ported from design growthos/Modals.EmailModal (incl. the in-modal two-pane
   Insert-Template panel). Wired to the real apiServices.sendEmail + the redux
   template library (getAllTemplate) + the redux email refresh. Also supports the
   read-only `preview` mode used by the Emails tab (localData.type === 'preview').
   Footer formatting icons are decorative (as in the design). */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../../services/service';
import { getUser, getAllTemplate } from '../../../redux/selector';
import { fetchEmailAndAppendToModule, fetchDataAndUpdateStore } from '../../../redux/actions';
import useFetchIfNeeded from '../../../redux/useFetchIfNeeded';
import { DetailDateFormatter } from '../../../shared/controls/formatters';
import { Ic } from "../../../shared/component/GosIcon";


const TOOLS = ['bold', 'sparkle', 'image', 'emoji', 'paperclip', 'link'];
const isHtml = (s) => /<[a-z!][\s\S]*>/i.test(String(s || ''));
const stripHtml = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const fmtDate = (v) => {
  if (!v) return '';
  try { return DetailDateFormatter(v); } catch (e) { return ''; }
};

/* ---------- in-modal template picker (search list + live preview) ---------- */
function TemplatePicker({ templates, lead, onInsert, onClose }) {
  const [q, setQ] = useState('');

  const fillMerge = (str) =>
    String(str || '')
      .replace(/\{\{\s*firstName\s*\}\}/gi, lead.firstName || (lead.fullName || '').split(' ')[0] || '')
      .replace(/\{\{\s*lastName\s*\}\}/gi, lead.lastName || '')
      .replace(/\{\{\s*fullName\s*\}\}/gi, lead.fullName || '')
      .replace(/\{\{\s*(company|companyName)\s*\}\}/gi, lead.companyName || '')
      .replace(/\{\{\s*email\s*\}\}/gi, lead.email || '')
      .replace(/\{\{\s*title\s*\}\}/gi, lead.title || '')
      .replace(/\{\{\s*tier\s*\}\}/gi, lead.tier || '');

  const bodyOf = (t) => t.body || t.content || t.bodyText || (t.htmlContent ? stripHtml(t.htmlContent) : '');

  const list = templates.filter(
    (t) =>
      !q.trim() ||
      [t.name, t.subject, t.templateType].some((f) => String(f || '').toLowerCase().includes(q.toLowerCase()))
  );
  const [selId, setSelId] = useState(list[0]?.id || null);
  useEffect(() => {
    if (!list.find((t) => t.id === selId)) setSelId(list[0]?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, templates]);
  const sel = templates.find((t) => t.id === selId) || null;

  return (
    <div className="gos-tmpl-panel">
      <div className="gos-tmpl-panel-head">
        <div>
          <div className="gos-tmpl-panel-title">Insert Template</div>
          <div className="gos-tmpl-faint">{templates.length} template{templates.length === 1 ? '' : 's'}</div>
        </div>
        <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={onClose} aria-label="Close">
          <Ic name="x" size={18} />
        </button>
      </div>
      <div className="gos-tmpl-panel-body">
        <div className="gos-tmpl-list-col">
          <div className="gos-tmpl-search">
            <Ic name="search" size={15} />
            <input placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          </div>
          <div className="gos-tmpl-list">
            {list.length === 0 ? <div className="gos-tmpl-empty">No templates match.</div> : null}
            {list.map((t) => (
              <button
                key={t.id}
                type="button"
                className={'gos-tmpl-item' + (selId === t.id ? ' on' : '')}
                onClick={() => setSelId(t.id)}
                onDoubleClick={() => onInsert(t, fillMerge, bodyOf)}
              >
                <div className="gos-tmpl-item-name">{t.name}</div>
                <div className="gos-tmpl-item-subj">{fillMerge(t.subject)}</div>
                <div className="gos-tmpl-item-meta">
                  {t.templateType ? <span className="gos-tmpl-tag">{t.templateType}</span> : null}
                  {t.associatedModule ? <span className="gos-tmpl-faint">{t.associatedModule}</span> : null}
                  {fmtDate(t.updatedDateTime || t.createdDateTime) ? (
                    <span className="gos-tmpl-faint">· {fmtDate(t.updatedDateTime || t.createdDateTime)}</span>
                  ) : null}
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="gos-tmpl-preview-col">
          {sel ? (
            <>
              <div className="gos-tmpl-preview-scroll">
                <div className="gos-tmpl-prev-label">Subject</div>
                <div className="gos-tmpl-prev-subject">{fillMerge(sel.subject) || '—'}</div>
                <div className="gos-tmpl-prev-label">Preview</div>
                {bodyOf(sel) ? (
                  <div className="gos-tmpl-prev-body">{fillMerge(bodyOf(sel))}</div>
                ) : (
                  <div className="gos-tmpl-prev-note">
                    This is a designed (HTML) template — its full content is applied when inserted.
                  </div>
                )}
              </div>
              <div className="gos-tmpl-preview-foot">
                <span className="gos-tmpl-faint">Merge fields fill from the recipient</span>
                <button type="button" className="gos-btn gos-btn-primary gos-btn-sm" onClick={() => onInsert(sel, fillMerge, bodyOf)}>
                  <Ic name="check" size={15} /> Insert template
                </button>
              </div>
            </>
          ) : (
            <div className="gos-tmpl-empty gos-tmpl-empty-center">Select a template to preview</div>
          )}
        </div>
      </div>
    </div>
  );
}

const ComposeEmail = ({ localData = {}, module, handleClose }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(getUser);
  const userEmail = userInfo?.user?.email || '';
  const mod = module || localData.module || 'Lead';
  const isPreview = localData.type === 'preview';

  // template library (shared redux store)
  useFetchIfNeeded(getAllTemplate, fetchDataAndUpdateStore, { key: 'template' });
  const allTemplates = useSelector(getAllTemplate) || [];
  const templates = allTemplates.filter((t) =>
    mod ? String(t?.associatedModule || '').toLowerCase().includes(String(mod).toLowerCase()) : true
  );

  const emailString = process.env.REACT_APP_FROM_ADDRESSES || '';
  const fromOptions = emailString.split(',').map((e) => e.trim()).filter(Boolean);

  const [from, setFrom] = useState(localData.fromAddress || fromOptions[0] || '');
  const [to, setTo] = useState(localData.toAddress || localData.email || '');
  const [cc, setCc] = useState(localData.ccAddress || '');
  const [bcc, setBcc] = useState(localData.bccAddress || '');
  const [showCc, setShowCc] = useState(!!localData.ccAddress);
  const [showBcc, setShowBcc] = useState(!!localData.bccAddress);
  const [subject, setSubject] = useState(localData.subject || '');
  const [message, setMessage] = useState(localData.message || '');
  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [tmplOpen, setTmplOpen] = useState(false);
  const [fromOpen, setFromOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const sendDisabled = !from || !String(to).trim();

  const buildPayload = (overrideTo) => ({
    fromAddress: from,
    toAddress: overrideTo || to,
    ccAddress: cc || null,
    bccAddress: bcc || null,
    subject,
    message,
    templateId: templateId || null,
    module: mod,
    moduleId: localData.id,
    status: 'Pending',
    isDraft: false,
    scheduledTime: null,
    optOut: localData.optOut ? '1' : '0',
  });

  const fire = (payload, { isTest } = {}) => {
    setSending(true);
    apiServices
      .sendEmail(payload)
      .then((response) => {
        if (response.status === 200) {
          toast.success(isTest ? 'Test email sent!' : 'Email sent successfully!', { position: 'top-right' });
          if (!isTest) {
            setTimeout(() => {
              dispatch(fetchEmailAndAppendToModule({ moduleName: mod, id: localData.id }));
            }, 1500);
            handleClose();
          } else {
            setSending(false);
          }
        } else {
          setSending(false);
          const msgs = [];
          for (const key in response.errors) {
            if (response.errors.hasOwnProperty(key)) msgs.push(...response.errors[key]);
          }
          msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
        }
      })
      .catch(() => {
        setSending(false);
        toast.error('An unexpected error occurred.', { position: 'top-right' });
      });
  };

  const send = () => {
    if (sendDisabled || sending) return;
    fire(buildPayload());
  };
  const sendTest = () => {
    if (!from || sending) return;
    if (!userEmail) {
      toast.error('No test address available for your account.', { position: 'top-right' });
      return;
    }
    fire(buildPayload(userEmail), { isTest: true });
  };

  const insertTemplate = (t, fillMerge, bodyOf) => {
    if (t.subject) setSubject(fillMerge(t.subject));
    const b = bodyOf(t);
    if (b) setMessage(fillMerge(b));
    if (t.id) setTemplateId(t.id);
    setTemplateName(t.name || 'Template');
    setTmplOpen(false);
  };

  /* ---------- read-only preview of a sent email ---------- */
  if (isPreview) {
    const rows = [
      ['From', localData.fromAddress],
      ['To', localData.toAddress],
      localData.ccAddress ? ['Cc', localData.ccAddress] : null,
      ['Subject', localData.subject],
    ].filter(Boolean);
    return (
      <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
        <div className="gos-em-modal" role="dialog" aria-modal="true" aria-label="Email">
          <div className="gos-em-head">
            <div className="gos-em-title">{localData.subject || 'Email'}</div>
            <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={handleClose} aria-label="Close">
              <Ic name="x" size={18} />
            </button>
          </div>
          {rows.map(([lab, v], i) => (
            <div className="gos-em-row" key={i}>
              <span className="gos-em-lab">{lab}</span>
              <span className="gos-em-static">{v || '—'}</span>
            </div>
          ))}
          <div className="gos-em-body gos-em-preview">
            {isHtml(localData.message) ? (
              <div className="gos-em-preview-html" dangerouslySetInnerHTML={{ __html: localData.message }} />
            ) : (
              <div className="gos-em-preview-text">{localData.message || 'No content.'}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) handleClose(); }}>
      <div className="gos-em-modal" role="dialog" aria-modal="true" aria-label="Compose email">
        <div className="gos-em-head">
          <div className="gos-em-title">Compose New Email</div>
          <div className="gos-em-head-actions">
            <button type="button" className="gos-lr-ghost-btn" onClick={sendTest} disabled={sending || !from}>
              Send Test Email
            </button>
            <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={handleClose} aria-label="Close">
              <Ic name="x" size={18} />
            </button>
          </div>
        </div>

        <div className="gos-em-row">
          <span className="gos-em-lab">From</span>
          <div className="gos-em-fromdd">
            <button type="button" className="gos-em-from-btn" onClick={() => setFromOpen((o) => !o)} disabled={!fromOptions.length}>
              <span className="gos-em-from-val">{from || 'No sender configured'}</span>
              <Ic name="chevD" size={15} />
            </button>
            {fromOpen ? (
              <>
                <div className="gos-em-from-backdrop" onClick={() => setFromOpen(false)} />
                <div className="gos-em-from-menu">
                  {fromOptions.map((e) => (
                    <button
                      key={e}
                      type="button"
                      className={'gos-em-from-item' + (e === from ? ' on' : '')}
                      onClick={() => { setFrom(e); setFromOpen(false); }}
                    >
                      <span className="gos-em-from-item-txt">{e}</span>
                      {e === from ? <Ic name="check" size={15} /> : null}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="gos-em-row">
          <span className="gos-em-lab">To</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@email.com" />
          {!showCc ? <button type="button" className="gos-em-chip" onClick={() => setShowCc(true)}>Cc</button> : null}
          {!showBcc ? <button type="button" className="gos-em-chip" onClick={() => setShowBcc(true)}>Bcc</button> : null}
        </div>

        {showCc ? (
          <div className="gos-em-row">
            <span className="gos-em-lab">Cc</span>
            <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@email.com; …" autoFocus />
          </div>
        ) : null}
        {showBcc ? (
          <div className="gos-em-row">
            <span className="gos-em-lab">Bcc</span>
            <input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="bcc@email.com; …" autoFocus />
          </div>
        ) : null}

        <div className="gos-em-row">
          <span className="gos-em-lab">Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
        </div>

        <div className="gos-em-body">
          {!message ? (
            <div className="gos-em-aihint">
              <Ic name="sparkle" size={15} /> Help me write
            </div>
          ) : null}
          <textarea placeholder="Write your message…" value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>

        <div className="gos-em-foot">
          <div className="gos-em-foot-l">
            <button type="button" className={'gos-lr-ghost-btn' + (templateName ? ' has-tmpl' : '')} onClick={() => setTmplOpen(true)}>
              <Ic name="template" size={15} /> {templateName || 'Insert Template'}
            </button>
            <span className="gos-em-divider" />
            {TOOLS.map((t) => (
              <button key={t} type="button" className="gos-em-tool" tabIndex={-1} aria-hidden="true">
                <Ic name={t} size={16} />
              </button>
            ))}
          </div>
          <div className="gos-em-foot-r">
            <button type="button" className="gos-btn gos-btn-primary" onClick={send} disabled={sendDisabled || sending}>
              {sending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending…
                </>
              ) : (
                <>
                  Send Email <Ic name="send" size={15} />
                </>
              )}
            </button>
          </div>
        </div>

        {tmplOpen ? (
          <TemplatePicker templates={templates} lead={localData} onInsert={insertTemplate} onClose={() => setTmplOpen(false)} />
        ) : null}
      </div>
    </div>
  );
};

export default ComposeEmail;
